<script setup lang="ts">
import { computed, ref } from 'vue'
import ResourcePickerModal from '@/features/virtual-resource/components/ResourcePickerModal.vue'
import { AVG_RESOURCE_PATH_MIME, type ResourceCategoryKey } from '@/features/virtual-resource/constants'
import { useWorkspaceStore } from '@/store/workspace'
import {
  resolveDisplayPathFromFileName,
  resolveFullDisplayPathFromFileName,
  resolveRelativePathFromDisplayPath,
  resolveStoredPathFromDisplayPath,
} from '@/features/virtual-resource/fileNamePath'

type ResourceSelectDisplayMode = 'raw' | 'mapped' | 'full'
type ResourceSelectStoreMode = 'raw' | 'relative' | 'mapped' | 'engine'

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    category?: ResourceCategoryKey
    pickerTitle?: string
    baseFolderPath?: string
    fallbackFolderPath?: string
    displayMode?: ResourceSelectDisplayMode
    storeMode?: ResourceSelectStoreMode
  }>(),
  {
    category: 'graphic',
    displayMode: 'full',
    storeMode: 'relative',
    readonly: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const pickerOpen = ref(false)
const pickerSearch = ref('')
const dragActive = ref(false)
let dragEnterDepth = 0
const workspace = useWorkspaceStore()

function normalizeText(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\\/g, '/')
}

const displayValue = computed(() => {
  const root = workspace.virtualResourceRoots[props.category]
  if (props.displayMode === 'raw') return props.modelValue ?? ''
  if (props.displayMode === 'mapped') {
    return resolveDisplayPathFromFileName(root, props.category, props.modelValue ?? '', 'mapped', props.baseFolderPath)
  }
  return resolveFullDisplayPathFromFileName(root, props.category, props.modelValue ?? '', 'mapped', props.baseFolderPath)
})

function toStoredValue(value: string): string {
  const root = workspace.virtualResourceRoots[props.category]
  if (props.storeMode === 'mapped') {
    return resolveStoredPathFromDisplayPath(root, props.category, value, 'mapped', props.baseFolderPath)
  }
  if (props.storeMode === 'engine') {
    return resolveStoredPathFromDisplayPath(root, props.category, value, 'engine', props.baseFolderPath)
  }
  if (props.storeMode === 'relative') {
    return resolveRelativePathFromDisplayPath(root, props.category, value, props.baseFolderPath)
  }
  return normalizeText(value)
}

function emitValue(value: string) {
  emit('update:modelValue', toStoredValue(value))
}

function onDragOver(event: DragEvent) {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

function onDragEnter(event: DragEvent) {
  if (props.disabled || props.readonly) return
  event.preventDefault()
  dragEnterDepth += 1
  dragActive.value = true
}

function onDragLeave() {
  if (props.disabled || props.readonly) return
  dragEnterDepth = Math.max(0, dragEnterDepth - 1)
  if (dragEnterDepth === 0) {
    dragActive.value = false
  }
}

function onDrop(event: DragEvent) {
  if (props.disabled || props.readonly) return
  event.preventDefault()
  dragEnterDepth = 0
  dragActive.value = false
  const dt = event.dataTransfer
  if (!dt) return
  const raw = dt.getData(AVG_RESOURCE_PATH_MIME).trim()
  if (!raw) return
  emitValue(raw)
}

function openPicker() {
  if (props.disabled) return
  pickerSearch.value = ''
  pickerOpen.value = true
}
</script>

<template>
  <div
    class="resource-select-field"
    :class="{
      'resource-select-field--disabled': disabled,
      'resource-select-field--drag-active': dragActive,
    }"
  >
    <input
      class="resource-select-input"
      type="text"
      :value="displayValue"
      :disabled="disabled"
      :readonly="true"
      :placeholder="placeholder ?? '请选择资源'"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    />
    <button
      type="button"
      class="resource-select-trigger"
      :disabled="disabled"
      :title="pickerTitle ?? '选择资源'"
      @click="openPicker"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M11.6 1.4a1.5 1.5 0 0 1 2.1 2.1l-7.3 7.3-3 0.8 0.8-3 7.4-7.2Zm1.4 2.1a.5.5 0 0 0-.7-.7L5.2 9.9l-0.4 1.4 1.4-0.4L13 3.5ZM2 13h12v1H2z"
          fill="currentColor"
        />
      </svg>
    </button>

    <ResourcePickerModal
      v-if="pickerOpen"
      v-model:show="pickerOpen"
      :category="category"
      :title="pickerTitle ?? '选择资源'"
      :search="pickerSearch"
      :selected-path="modelValue"
      :base-folder-path="baseFolderPath"
      :fallback-folder-path="fallbackFolderPath"
      @update:search="pickerSearch = $event"
      @select="emitValue"
    />
  </div>
</template>

<style scoped>
.resource-select-field {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  box-sizing: border-box;
}

.resource-select-input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 4px 34px 4px 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  font-size: 12px;
  line-height: 20px;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-select-input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.resource-select-field--drag-active .resource-select-input {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected, var(--theme-bg-card));
}

.resource-select-trigger {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 26px;
  height: calc(100% - 2px);
  padding: 0;
  border: 0;
  border-left: 1px solid var(--theme-border);
  background: var(--theme-bg-surface);
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.resource-select-trigger:hover:not(:disabled) {
  color: var(--theme-text-heading);
  background: var(--theme-bg-raised);
}

.resource-select-trigger:disabled,
.resource-select-input:disabled {
  cursor: not-allowed;
}

.resource-select-trigger svg {
  width: 14px;
  height: 14px;
}

.resource-select-field--disabled .resource-select-input {
  opacity: 0.72;
}
</style>
